import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type {
  MemberContext,
  MemberContextUser,
  OrgMembership,
  OrgType,
  PersonaRole,
} from '@/shared/domain'
import {
  ACCOUNT_LOAD_RETRY_DELAY_MS,
  isOwnerRole,
  isTrackingOnlyRole,
  normalizeJurisdictionState,
  retryOnce,
} from '@/shared/domain'
import { reportError, setPersonaSnapshot } from '@/shared/infra/observability'
import { clearImpersonation, endImpersonationClient, readImpersonation } from '@/shared/infra/impersonation'
import { clearOrgScopedQueries } from '@/shared/infra/query'
import { OrgContextCtx, useAuth } from '@/shared/ui/hooks'
import { useToast } from './toast-context'
import type { LoadMemberContext } from '@/shared/domain'
import type { RegisterTabCloseRevoke, RevokeImpersonation } from '@/modules/auth/use-cases'

const SELECTED_ORG_STORAGE_KEY = 'cc-selected-org-id'
const MEMBER_CONTEXT_STORAGE_KEY = 'cc-member-context'
const MEMBER_CONTEXT_MAX_AGE_MS = 12 * 60 * 60 * 1000
const REVOKE_TIMEOUT_MS = 3000
const CONTEXT_LOAD_TOAST_TITLE = 'Connection lost'
const CONTEXT_LOAD_FALLBACK_MESSAGE = "We couldn't refresh your account. Check your connection and try again."

type PersonaProviderProps = {
  onLoadMemberContext: LoadMemberContext
  onRevokeImpersonation: RevokeImpersonation
  onRegisterTabCloseRevoke: RegisterTabCloseRevoke
  children: React.ReactNode
}

function readStoredOrgId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(SELECTED_ORG_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredOrgId(orgId: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (orgId === null) {
      window.localStorage.removeItem(SELECTED_ORG_STORAGE_KEY)
    } else {
      window.localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgId)
    }
  } catch {
    return
  }
}

type CachedMemberContext = {
  userId: string
  cachedAt: number
  context: MemberContext
}

function readCachedMemberContext(userId: string): MemberContext | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(MEMBER_CONTEXT_STORAGE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw) as CachedMemberContext
    if (cached.userId !== userId) return null
    if (Date.now() - cached.cachedAt > MEMBER_CONTEXT_MAX_AGE_MS) return null
    return cached.context
  } catch {
    return null
  }
}

function writeCachedMemberContext(userId: string, context: MemberContext): void {
  if (typeof window === 'undefined') return
  try {
    const cached: CachedMemberContext = { userId, cachedAt: Date.now(), context }
    window.localStorage.setItem(MEMBER_CONTEXT_STORAGE_KEY, JSON.stringify(cached))
  } catch {
    return
  }
}

function deriveIsManager(orgType: OrgType | null, role: PersonaRole | null): boolean {
  return isOwnerRole(role ?? '', orgType ?? undefined)
}

function toOrgMemberships(context: MemberContext): OrgMembership[] {
  const entries = context.orgMemberships ?? []
  const all = entries
    .filter(
      (m) =>
        m.membership.status === 'active' &&
        m.org.orgType !== 'crew' &&
        (m.org.orgType === 'club' || m.org.orgType === 'outfitter') &&
        !isTrackingOnlyRole(m.membership.role, m.org.orgType) &&
        isOwnerRole(m.membership.role, m.org.orgType),
    )
    .map<OrgMembership>((m) => ({
      orgId: m.membership.orgId,
      orgType: m.org.orgType === 'club' ? 'club' : 'outfitter',
      role: m.membership.role,
      orgName: m.org.name,
      timezone: m.org.timezone,
      badgeUrl: m.org.badgeUrl,
      bannerUrl: m.org.bannerUrl,
      subscriptionStatus: m.org.subscriptionStatus,
      subscriptionPastDueSince: m.org.subscriptionPastDueSince,
      subscriptionCancelAt: m.org.subscriptionCancelAt,
      subscriptionCurrentPeriodEnd: m.org.subscriptionCurrentPeriodEnd,
      defaultCardBrand: m.org.defaultCardBrand,
      defaultCardLast4: m.org.defaultCardLast4,
      defaultCardExpMonth: m.org.defaultCardExpMonth,
      defaultCardExpYear: m.org.defaultCardExpYear,
      orgState: m.org.orgState,
      clubFeaturesEnabled: m.org.clubFeaturesEnabled,
      clubFeaturesActive: m.org.clubFeaturesActive,
    }))
  return all
}

function resolveSelectedMembership(
  memberships: OrgMembership[],
  preferredOrgId: string | null,
): OrgMembership | null {
  if (memberships.length === 0) return null
  if (preferredOrgId) {
    const match = memberships.find((m) => m.orgId === preferredOrgId)
    if (match) return match
  }
  return memberships[0]
}

function deriveUserDisplayName(user: MemberContextUser | undefined): string | null {
  if (!user) return null
  const displayName = user.displayName?.trim()
  if (displayName) return displayName
  const fullName = [user.firstName, user.lastName]
    .map((part) => part?.trim())
    .filter((part) => Boolean(part))
    .join(' ')
  return fullName ? fullName : null
}

export function PersonaProvider({
  onLoadMemberContext,
  onRevokeImpersonation,
  onRegisterTabCloseRevoke,
  children,
}: PersonaProviderProps) {
  const { user, isLoading: isAuthLoading } = useAuth()
  const queryClient = useQueryClient()
  const [memberships, setMemberships] = useState<OrgMembership[]>([])
  const [rawContext, setRawContext] = useState<MemberContext | null>(null)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => readStoredOrgId())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadFailed, setLoadFailed] = useState<boolean>(false)
  const [impersonation, setImpersonation] = useState(() => readImpersonation())
  const { errorWithRetry } = useToast()
  const consecutiveFailuresRef = useRef(0)
  const retryRef = useRef<() => void>(() => {})

  const fetchContext = useCallback(async () => {
    setIsLoading(true)
    try {
      const context = await onLoadMemberContext({ includeLegalDocs: true })
      setRawContext(context)
      setMemberships(toOrgMemberships(context))
      setLoadFailed(false)
      if (user && !readImpersonation()) writeCachedMemberContext(user.id, context)
      consecutiveFailuresRef.current = 0
    } finally {
      setIsLoading(false)
    }
  }, [onLoadMemberContext, user])

  const showRetryToast = useCallback(
    (message: string) => {
      errorWithRetry(CONTEXT_LOAD_TOAST_TITLE, message, () => retryRef.current())
    },
    [errorWithRetry],
  )

  const retryLoad = useCallback(() => {
    void fetchContext().catch((err) => {
      showRetryToast(reportError(err, { fallbackMessage: CONTEXT_LOAD_FALLBACK_MESSAGE }))
    })
  }, [fetchContext, showRetryToast])

  useEffect(() => {
    retryRef.current = retryLoad
  }, [retryLoad])

  const previousUserIdRef = useRef<string | null>(user?.id ?? null)
  useEffect(() => {
    const nextUserId = user?.id ?? null
    if (previousUserIdRef.current !== null && previousUserIdRef.current !== nextUserId) {
      queryClient.clear()
    }
    previousUserIdRef.current = nextUserId
  }, [user, queryClient])

  useEffect(() => {
    if (!user) {
      setMemberships([])
      setRawContext(null)
      setIsLoading(false)
      setLoadFailed(false)
      return
    }
    let cancelled = false
    const isImpersonating = readImpersonation() !== null
    const storedContext = isImpersonating ? null : readCachedMemberContext(user.id)
    const cachedMemberships = storedContext ? toOrgMemberships(storedContext) : []
    const cached = cachedMemberships.length > 0 ? storedContext : null
    if (cached) {
      setRawContext(cached)
      setMemberships(cachedMemberships)
      setIsLoading(false)
    } else {
      setIsLoading(true)
    }
    retryOnce(() => onLoadMemberContext({ includeLegalDocs: true }), ACCOUNT_LOAD_RETRY_DELAY_MS)
      .then((context) => {
        if (cancelled) return
        consecutiveFailuresRef.current = 0
        setRawContext(context)
        setMemberships(toOrgMemberships(context))
        setLoadFailed(false)
        if (!isImpersonating) writeCachedMemberContext(user.id, context)
      })
      .catch((err) => {
        if (cancelled) return
        const message = reportError(err, { fallbackMessage: CONTEXT_LOAD_FALLBACK_MESSAGE })
        if (!cached) setLoadFailed(true)
        consecutiveFailuresRef.current += 1
        if (consecutiveFailuresRef.current > 1) {
          showRetryToast(message)
        }
      })
      .finally(() => {
        if (cancelled) return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user, onLoadMemberContext, showRetryToast])

  const selectedMembership = useMemo(
    () =>
      impersonation
        ? resolveSelectedMembership(memberships, impersonation.orgId)
        : resolveSelectedMembership(memberships, selectedOrgId),
    [memberships, selectedOrgId, impersonation],
  )

  const selectOrg = useCallback(
    (orgId: string | null) => {
      if (impersonation) return
      setSelectedOrgId(orgId)
      writeStoredOrgId(orgId)
      clearOrgScopedQueries(queryClient)
    },
    [impersonation, queryClient],
  )

  const stopImpersonation = useCallback(async () => {
    try {
      await Promise.race([
        onRevokeImpersonation(),
        new Promise<void>((resolve) => setTimeout(resolve, REVOKE_TIMEOUT_MS)),
      ])
    } catch (err) {
      reportError(err, {
        fallbackMessage: 'Failed to end the impersonation session on the server.',
      })
    } finally {
      await endImpersonationClient()
      setImpersonation(null)
      queryClient.clear()
    }
  }, [onRevokeImpersonation, queryClient])

  useEffect(() => {
    if (isAuthLoading) return
    if (!user && impersonation) {
      clearImpersonation()
      setImpersonation(null)
    }
  }, [isAuthLoading, user, impersonation])

  useEffect(() => {
    if (!impersonation || !user) return
    const unregister = onRegisterTabCloseRevoke()
    return unregister
  }, [impersonation, user, onRegisterTabCloseRevoke])

  const refresh = useCallback(async () => {
    if (!user) return
    await fetchContext()
  }, [user, fetchContext])

  const value = useMemo(() => {
    const orgId = selectedMembership?.orgId ?? null
    const orgType: OrgType | null =
      selectedMembership?.orgType === 'club' || selectedMembership?.orgType === 'outfitter'
        ? selectedMembership.orgType
        : null
    const role = selectedMembership?.role ?? null
    return {
      userId: rawContext?.user?.id ?? null,
      orgId,
      orgType,
      role,
      isManager: deriveIsManager(orgType, role),
      orgTimezone: selectedMembership?.timezone,
      memberships,
      subscriptionStatus: selectedMembership?.subscriptionStatus ?? null,
      subscriptionPastDueSince: selectedMembership?.subscriptionPastDueSince,
      subscriptionCancelAt: selectedMembership?.subscriptionCancelAt,
      subscriptionCurrentPeriodEnd: selectedMembership?.subscriptionCurrentPeriodEnd,
      defaultCardBrand: selectedMembership?.defaultCardBrand,
      defaultCardLast4: selectedMembership?.defaultCardLast4,
      defaultCardExpMonth: selectedMembership?.defaultCardExpMonth,
      defaultCardExpYear: selectedMembership?.defaultCardExpYear,
      orgState: normalizeJurisdictionState(selectedMembership?.orgState),
      clubFeaturesEnabled: selectedMembership?.clubFeaturesEnabled ?? false,
      clubFeaturesActive:
        selectedMembership?.clubFeaturesActive ??
        (orgType !== 'outfitter' ? true : selectedMembership?.clubFeaturesEnabled === true),
      selectOrg,
      isLoading: isLoading || isAuthLoading,
      loadFailed,
      refresh,
      userDisplayName: deriveUserDisplayName(rawContext?.user),
      pendingLegalDocuments: rawContext?.pendingLegalDocuments ?? [],
      isImpersonating: impersonation !== null,
      impersonationOrgName: impersonation?.orgName ?? null,
      impersonationExpiresAt: impersonation?.expiresAt ?? null,
      stopImpersonation,
    }
  }, [
    selectedMembership,
    memberships,
    rawContext,
    selectOrg,
    isLoading,
    isAuthLoading,
    loadFailed,
    refresh,
    impersonation,
    stopImpersonation,
  ])

  useEffect(() => {
    setPersonaSnapshot({
      orgId: value.orgId,
      orgType: value.orgType,
      role: value.role,
    })
  }, [value.orgId, value.orgType, value.role])

  return <OrgContextCtx.Provider value={value}>{children}</OrgContextCtx.Provider>
}
